import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  Grid,
  Text,
  SimpleGrid,
  Spinner,
  useColorModeValue,
  Icon,
  Button,
  Badge,
  HStack,
  VStack,
  Avatar,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { MdArrowBack, MdTrendingUp, MdTrendingDown, MdShowChart } from "react-icons/md";
import Card from "components/card/Card.js";
import LineAreaChart from "components/charts/LineAreaChart";

export default function Analytics() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const networkData = location.state?.network;

  const [priceData, setPriceData] = useState([]);
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "#A0AEC0");
  const brandColor = useColorModeValue("brand.500", "#7551FF");
  const chartGridColor = useColorModeValue("#E2E8F0", "#4A5568");
  const tooltipBg = useColorModeValue("white", "navy.700");
  const tooltipTextColor = useColorModeValue("secondaryGray.900", "white");
  const chartBorderColor = useColorModeValue("#E2E8F0", "#4A5568");
  const secondaryChartColor = useColorModeValue("green.500", "#48BB78");
  const accentColor = useColorModeValue("purple.500", "#9F7AEA");
  const cardBg = useColorModeValue("white", "navy.800");
  const chartLineColor = useColorModeValue("#422AFB", "#7551FF");

  useEffect(() => {
    if (networkData) {
      fetchAnalyticsData();
    } else {
      navigate('/admin/networks');
    }
  }, [networkData]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      if (networkData?.historical_data && networkData.historical_data.length > 0) {
        const realPriceData = generateRealPriceData(networkData.historical_data);
        const realMarketData = generateRealMarketData(networkData);
        setPriceData(realPriceData);
        setMarketData(realMarketData);
      } else {
        const mockPriceData = generateMockPriceData();
        const mockMarketData = generateMockMarketData();
        setPriceData(mockPriceData);
        setMarketData(mockMarketData);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const generateRealPriceData = (historicalData) => {
    return historicalData
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .map(item => ({ x: new Date(item.createdAt).getTime(), y: parseFloat(item.to_amount) }))
      .filter(p => Number.isFinite(p.x) && Number.isFinite(p.y));
  };

  const generateRealMarketData = (networkData) => {
    const historical = networkData.historical_data;
    if (!historical || historical.length === 0) {
      return generateMockMarketData();
    }
    const sortedData = historical.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const currentPrice = parseFloat(sortedData[0].to_amount);
    const previousPrice = sortedData.length > 1 ? parseFloat(sortedData[1].to_amount) : currentPrice;
    const priceChange = currentPrice - previousPrice;
    const priceChangePercent = previousPrice !== 0 ? ((priceChange / previousPrice) * 100).toFixed(2) : "0.00";
    return {
      currentPrice,
      currentPriceUSD: networkData.default_amount ? parseFloat(networkData.default_amount) : 1,
      priceChange,
      priceChangePercent,
      exchangeRate: networkData.exchange_rate || networkData.fx_rate,
      baseCurrency: networkData.currency || 'NGN',
      change: networkData.change,
      changeSymbol: networkData.change_symbol,
      absoluteChange: networkData.absolute_change_amount,
      absoluteChangePercentage: networkData.absolute_change_percentage,
    };
  };

  const generateMockPriceData = () => {
    const data = [];
    const basePrice = Math.random() * 50000 + 10000;
    let currentPrice = basePrice;
    for (let i = 30; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const change = (Math.random() - 0.5) * 0.1;
      currentPrice = currentPrice * (1 + change);
      data.push({ x: date.getTime(), y: parseFloat(currentPrice.toFixed(2)) });
    }
    return data;
  };

  const generateMockMarketData = () => {
    const currentPrice = priceData.length > 0 ? priceData[priceData.length - 1].y : 45000;
    const previousPrice = priceData.length > 1 ? priceData[priceData.length - 2].y : 44000;
    const priceChange = currentPrice - previousPrice;
    const priceChangePercent = ((priceChange / previousPrice) * 100).toFixed(2);
    return {
      currentPrice,
      currentPriceUSD: 1.0,
      priceChange,
      priceChangePercent,
      exchangeRate: currentPrice,
      baseCurrency: 'NGN',
      change: priceChange >= 0 ? 'high' : 'low',
      changeSymbol: priceChange >= 0 ? '+' : '-',
      absoluteChange: Math.abs(priceChange).toFixed(2),
      absoluteChangePercentage: Math.abs(parseFloat(priceChangePercent)).toFixed(2),
    };
  };

  // Sanitize and prepare chart data
  const cleanedPriceData = React.useMemo(
    () => priceData.filter(p => Number.isFinite(p?.x) && Number.isFinite(p?.y)),
    [priceData]
  );
  const hasNoData = cleanedPriceData.length === 0;
  const hasSinglePoint = cleanedPriceData.length === 1;
  const paddedPriceData = React.useMemo(() => {
    if (hasNoData) return [];
    if (!hasSinglePoint) return cleanedPriceData;
    const p = cleanedPriceData[0];
    const before = { x: p.x - 60 * 1000, y: p.y };
    return [before, p];
  }, [cleanedPriceData, hasNoData, hasSinglePoint]);
  const yBounds = React.useMemo(() => {
    if (hasNoData) return {};
    const ys = cleanedPriceData.map(p => p?.y).filter(Number.isFinite);
    if (ys.length === 0) return {};
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const range = maxY - minY;
    if (hasSinglePoint || range === 0) {
      const base = minY;
      const pad = base === 0 ? 1 : Math.max(Math.abs(base) * 0.01, 0.01);
      return { min: base - pad, max: base + pad };
    }
    const margin = Math.max(range * 0.05, 0.01);
    return { min: minY - margin, max: maxY + margin };
  }, [hasNoData, hasSinglePoint, cleanedPriceData]);
  const xBounds = React.useMemo(() => {
    if (hasSinglePoint && !hasNoData && cleanedPriceData[0]) {
      return { min: cleanedPriceData[0].x - 60 * 1000, max: cleanedPriceData[0].x + 60 * 1000 };
    }
    return {};
  }, [hasSinglePoint, hasNoData, cleanedPriceData]);

  // Chart configurations
  const priceChartOptions = {
    chart: {
      toolbar: { show: false },
      background: 'transparent',
      foreColor: textColorSecondary,
      animations: { enabled: paddedPriceData.length > 1 },
    },
    tooltip: {
      enabled: true,
      theme: useColorModeValue('light', 'dark'),
      style: { fontSize: "12px", fontFamily: "DM Sans", color: tooltipTextColor },
      custom: function ({ series, seriesIndex, dataPointIndex }) {
        const safeSeries = Array.isArray(series) && series[seriesIndex] ? series[seriesIndex] : [];
        const value = typeof safeSeries[dataPointIndex] !== 'undefined' ? safeSeries[dataPointIndex] : null;
        const currency = networkData?.currency || 'NGN';
        const formattedValue = value !== null ? parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
        return `
          <div style="
            background: ${tooltipBg}; 
            color: ${tooltipTextColor}; 
            padding: 10px; 
            border-radius: 6px;
            border: 1px solid ${chartBorderColor};
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          ">
            <strong>Price: ${currency === 'NGN' ? '₦' : '$'}${formattedValue}</strong>
          </div>
        `;
      },
      x: { format: "dd/MM/yy HH:mm" },
    },
    dataLabels: { enabled: false, style: { colors: [textColorSecondary] } },
    stroke: { curve: "smooth", colors: [chartLineColor], width: 3 },
    colors: [chartLineColor],
    markers: { size: 0, colors: [chartLineColor], strokeColors: chartLineColor, strokeWidth: 2, hover: { size: 6, sizeOffset: 3 } },
    xaxis: { type: "datetime", ...xBounds, labels: { style: { colors: [textColorSecondary], fontSize: "12px", fontFamily: "DM Sans" } }, axisBorder: { show: false, color: chartBorderColor }, axisTicks: { show: false, color: chartBorderColor }, title: { style: { color: textColorSecondary } } },
    yaxis: {
      labels: {
        style: { colors: [textColorSecondary], fontSize: "12px", fontFamily: "DM Sans" },
        formatter: function (val) {
          const currency = networkData?.currency || 'NGN';
          const symbol = currency === 'NGN' ? '₦' : '$';
          return symbol + parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
      },
      title: { style: { color: textColorSecondary } },
      ...yBounds,
    },
    legend: { show: false, labels: { colors: textColorSecondary } },
    grid: { show: true, borderColor: chartGridColor, strokeDashArray: 3, xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } } },
    fill: { type: "gradient", gradient: { shade: useColorModeValue("light", "dark"), type: "vertical", shadeIntensity: 0.5, gradientToColors: [chartLineColor], inverseColors: false, opacityFrom: 0.8, opacityTo: 0.1, stops: [0, 100] }, colors: [chartLineColor] },
    theme: { mode: useColorModeValue('light', 'dark') },
  };

  const priceChartData = [{ name: "Price", data: paddedPriceData, color: chartLineColor }];

  if (loading) {
    return (
      <Box pt={{ base: "180px", md: "80px", xl: "80px" }}>
        <Flex justifyContent="center" alignItems="center" height="400px">
          <VStack spacing={4}>
            <Spinner size="xl" color={brandColor} />
            <Text color={textColor} fontSize="lg">{t('Loading analytics data...')}</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "180px", md: "80px", xl: "80px" }}>
      {/* Header */}
      <Flex mb="20px" align="center" justify="space-between">
        <Button leftIcon={<Icon as={MdArrowBack} />} variant="ghost" color={textColor} onClick={() => navigate('/admin/networks')}>
          {t('Back to Networks')}
        </Button>
      </Flex>

      {/* Network Info Header */}
      <Card mb="20px" p={{ base: "15px", md: "20px" }}>
        <Flex align="center" justify="space-between">
          <HStack spacing={{ base: 2, md: 4 }} align="center">
            <Avatar src={networkData?.avatar} name={networkData?.currency_name} size={{ base: "md", md: "lg" }} flexShrink={0} />
            <VStack align="start" spacing={1}>
              <HStack flexWrap="wrap">
                <Text fontSize={{ base: "md", md: "2xl" }} fontWeight="bold" color={textColor}>{networkData?.currency_name}</Text>
                <Text fontSize={{ base: "xs", md: "lg" }} color={textColorSecondary}>({networkData?.currency_short})</Text>
                {networkData?.currency_symbol && (<Text fontSize={{ base: "sm", md: "xl" }} color={brandColor}>{networkData.currency_symbol}</Text>)}
              </HStack>
              <Text fontSize={{ base: "xs", md: "md" }} color={textColorSecondary}>Network: {networkData?.address_type}</Text>
              <HStack spacing={1} flexWrap="nowrap">
                {networkData?.mode?.map((mode, index) => (<Badge key={index} colorScheme="blue" variant="subtle" fontSize={{ base: "2xs", md: "xs" }}>{mode}</Badge>))}
                <Badge colorScheme={networkData?.status ? "green" : "red"} variant="subtle" fontSize={{ base: "2xs", md: "xs" }}>{networkData?.status ? t('Active') : t('Inactive')}</Badge>
              </HStack>
            </VStack>
          </HStack>

          {marketData && (
            <VStack align="end" spacing={1} flexShrink={0}>
              <VStack align="end" spacing={0}>
                <Text fontSize={{ base: "sm", md: "2xl" }} fontWeight="bold" color={textColor} whiteSpace="nowrap">₦{marketData.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                <Text fontSize={{ base: "2xs", md: "sm" }} color={textColorSecondary}>${parseFloat(marketData.currentPriceUSD).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD</Text>
              </VStack>
              <HStack>
                <Icon as={marketData.priceChange >= 0 ? MdTrendingUp : MdTrendingDown} color={marketData.priceChange >= 0 ? secondaryChartColor : "red.400"} boxSize={{ base: 3, md: 5 }} />
                <Text fontSize={{ base: "xs", md: "md" }} color={marketData.priceChange >= 0 ? secondaryChartColor : "red.400"} fontWeight="semibold">{marketData.changeSymbol}{Math.abs(parseFloat(marketData.priceChangePercent)).toFixed(2)}%</Text>
              </HStack>
              {marketData.absoluteChange && (<Text fontSize={{ base: "2xs", md: "sm" }} color={textColorSecondary}>{marketData.changeSymbol}₦{Math.abs(parseFloat(marketData.absoluteChange)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>)}
            </VStack>
          )}
        </Flex>
      </Card>

      {/* Market Stats */}
      {marketData && (
        <SimpleGrid columns={{ base: 2, md: 3 }} gap="20px" mb="20px">
          <Card p="20px" bg={cardBg} border="1px solid" borderColor={chartBorderColor}>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color={textColorSecondary}>{t('Current Price (USD)')}</Text>
              <Text fontSize="xl" fontWeight="bold" color={secondaryChartColor}>${parseFloat(marketData.currentPriceUSD).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
            </VStack>
          </Card>
          <Card p="20px" bg={cardBg} border="1px solid" borderColor={chartBorderColor}>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color={textColorSecondary}>{t('Exchange Rate')}</Text>
              <Text fontSize="xl" fontWeight="bold" color={brandColor}>₦{parseFloat(marketData.exchangeRate || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
            </VStack>
          </Card>
          <Card p="20px" bg={cardBg} border="1px solid" borderColor={chartBorderColor}>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color={textColorSecondary}>{t('24h Change')}</Text>
              <Text fontSize="xl" fontWeight="bold" color={marketData.priceChange >= 0 ? secondaryChartColor : "red.400"}>{marketData.changeSymbol}{Math.abs(parseFloat(marketData.absoluteChangePercentage || 0)).toFixed(2)}%</Text>
            </VStack>
          </Card>
        </SimpleGrid>
      )}

      {/* Charts Grid */}
      <SimpleGrid columns={{ base: 1 }} gap="20px" mb="20px">
        <Card p="20px">
          <Flex align="center" mb="20px">
            <Icon as={MdShowChart} color={brandColor} w={6} h={6} me={2} />
            <Text fontSize="lg" fontWeight="bold" color={textColor}>{t('Price History')} ({networkData?.currency || 'NGN'}) - {cleanedPriceData.length} {t('data points')}</Text>
          </Flex>
          <Box h="300px">
            {hasNoData ? (
              <Flex align="center" justify="center" h="100%" direction="column">
                <Text color={textColorSecondary} mb={2}>{t('No data available yet')}</Text>
                <Text color={textColorSecondary} fontSize="sm">{t('Once we have history for this network, the chart will appear here.')}</Text>
              </Flex>
            ) : (
              <LineAreaChart chartData={priceChartData} chartOptions={priceChartOptions} />
            )}
          </Box>
        </Card>
      </SimpleGrid>

      {/* Additional Analytics */}
      <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap="20px">
        <Card p="20px" bg={cardBg} border="1px solid" borderColor={chartBorderColor}>
          <Text fontSize="lg" fontWeight="bold" color={textColor} mb="15px">{t('Price Analysis')}</Text>
          <VStack align="stretch" spacing={3}>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>{t('Current Trend')}</Text>
              <Text color={marketData?.priceChange >= 0 ? secondaryChartColor : "red.400"} fontWeight="semibold">{marketData?.change === 'low' ? t('Bearish') : t('Bullish')}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>{t('Price Change')}</Text>
              <Text color={marketData?.priceChange >= 0 ? secondaryChartColor : "red.400"} fontWeight="semibold">{marketData?.changeSymbol}₦{Math.abs(parseFloat(marketData?.absoluteChange || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>{t('Base Rate')}</Text>
              <Text color={textColor} fontWeight="semibold">₦{parseFloat(networkData?.base_rate || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>{t('Ratio')}</Text>
              <Text color={accentColor} fontWeight="semibold">{networkData?.ratio || 'N/A'}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>{t('Data Points')}</Text>
              <Text color={brandColor} fontWeight="semibold">{networkData?.historical_data?.length || 0} {t('records')}</Text>
            </Flex>
          </VStack>
        </Card>

        <Card p="20px" bg={cardBg} border="1px solid" borderColor={chartBorderColor}>
          <Text fontSize="lg" fontWeight="bold" color={textColor} mb="15px">{t('Network Statistics')}</Text>
          <VStack align="stretch" spacing={3}>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>{t('Network Type')}</Text>
              <Text color={brandColor} fontWeight="semibold">{networkData?.address_type}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>{t('Environment')}</Text>
              <Text color={textColor} fontWeight="semibold">{networkData?.environment || t('Production')}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>{t('Min Amount')}</Text>
              <Text color={secondaryChartColor} fontWeight="semibold">{parseFloat(networkData?.min || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {networkData?.currency_short}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>{t('Max Amount')}</Text>
              <Text color={accentColor} fontWeight="semibold">{parseFloat(networkData?.max || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {networkData?.currency_short}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>{t('Base Amount')}</Text>
              <Text color={textColor} fontWeight="semibold">{parseFloat(networkData?.base_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {networkData?.currency_short}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>{t('Fee Percentage')}</Text>
              <Text color={textColor} fontWeight="semibold">{networkData?.transfer_network?.[0]?.fee_percentage || 'N/A'}%</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>{t('Arrival Time')}</Text>
              <Text color={textColor} fontWeight="semibold">{networkData?.transfer_network?.[0]?.arrival_time || 'N/A'}</Text>
            </Flex>
            {networkData?.destination_tag && (
              <Flex justify="space-between">
                <Text color={textColorSecondary}>{t('Destination Tag')}</Text>
                <Badge colorScheme="green" variant="solid">{t('Required')}</Badge>
              </Flex>
            )}
            <Flex justify="space-between">
              <Text color={textColorSecondary}>{t('Last Updated')}</Text>
              <Text color={textColor} fontWeight="semibold">{networkData?.updatedAt ? new Date(networkData.updatedAt).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).replace(/,/g, '').replace(/(\w+) (\d+) (\w+) (\d+)/, '$1 $2, $3 $4') : 'N/A'}</Text>
            </Flex>
          </VStack>
        </Card>
      </Grid>
    </Box>
  );
}

